import { RacemapColors } from '@racemap/utilities/consts/common';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type { PointObject } from '@racemap/utilities/types/geos';
import type { Feature, FeatureCollection, Point } from 'geojson';
import type { Immutable } from 'immer';
import { type FC, useMemo, useRef } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import type { NewDataProps } from '../BasicMap/BasicMap';
import { convertPointsToFeatures, injectFeatureIds } from '../helpers';

type PointProperties = Record<string, unknown> & {
  icon?: string;
};

type SymbolLayerPoints =
  | Feature<Point, PointProperties>
  | Array<Feature<Point, PointProperties>>
  | Array<PointObject>
  | FeatureCollection<Point, PointProperties>;

export interface Props {
  points: SymbolLayerPoints | Immutable<SymbolLayerPoints>;
  color?: string;
  fillColor?: string;
  lineWidth?: number;
  radius?: number;
  beforeId?: string;
  onNewData?: (props: NewDataProps) => void;
  onMouseEnter?: (feature: Feature<Point>, evt: React.MouseEvent<HTMLImageElement>) => void;
  onMouseLeave?: (feature: Feature<Point>, evt: React.MouseEvent<HTMLImageElement>) => void;
  onClick?: (
    feature: Feature<Point>,
    evt: Parameters<NonNullable<React.ComponentProps<typeof Marker>['onClick']>>[0],
  ) => void;
  autofocus?: boolean;
  id?: string;
}

export const SymbolLayer: FC<Props> = ({
  points,
  color = RacemapColors.PaleBlue,
  fillColor = RacemapColors.CloudBlue,
  lineWidth = 2,
  radius = 2,
  onMouseEnter,
  onMouseLeave,
  onClick,
  id,
}) => {
  const idRef = useRef(id || shortIdBuilder());

  const { features } = useMemo(
    () => convertPointsToFeatures(points, color, lineWidth, fillColor, radius),
    [points, color, lineWidth, fillColor, radius],
  );
  const featuresWithIdProperty = useMemo(
    () => injectFeatureIds(features).filter((f) => f.properties.icon != null),
    [features],
  );

  const marker = useMemo(
    () =>
      featuresWithIdProperty.map((f) => (
        <Marker
          key={`marker-${f.id}-${idRef.current}}`}
          longitude={f.geometry.coordinates[0]}
          latitude={f.geometry.coordinates[1]}
          anchor="bottom"
          pitchAlignment="viewport"
          onClick={(evt) => onClick?.(f, evt)}
        >
          <img
            src={`https://static-cdn.racemap.com/img/map-icons/${f.properties.icon}.svg`}
            alt={`Marker ${f.id}`}
            height="32"
            width="32"
            onMouseEnter={(evt: React.MouseEvent<HTMLImageElement>) => onMouseEnter?.(f, evt)}
            onMouseLeave={(evt: React.MouseEvent<HTMLImageElement>) => onMouseLeave?.(f, evt)}
          />
        </Marker>
      )),
    [featuresWithIdProperty],
  );

  return <>{marker}</>;
};
